import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePaginatedSupervisionTasks } from './index';

/**
 * Custom hook that manages cursor-based pagination for supervision tasks.
 * Encapsulates cursor history management and provides a page-number-based interface.
 *
 * @param status - The supervision status to filter by (APPROVED, REJECTED, etc.)
 * @returns An object containing tasks, loading states, pagination info, and handlers
 */
export function usePaginatedSupervisionTasksWithCursor(status: string) {
  // Cursor-based pagination state
  // Store cursor history: [undefined (page 1), cursor1 (page 2), cursor2 (page 3), ...]
  const [pageIndex, setPageIndex] = useState(0);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const currentCursor = cursors[pageIndex];

  // Fetch paginated tasks using the current cursor
  const {
    data: tasksResponse,
    isLoading,
    isRefetching,
    error,
  } = usePaginatedSupervisionTasks(status, currentCursor);

  // Extract data from response
  const tasks = tasksResponse?.tasks || [];
  const nextCursor = tasksResponse?.nextCursor;
  // Determine if there are more pages based on nextCursor presence
  // Backend returns empty string when no more pages, which is converted to undefined by API route
  // So nextCursor will be either a valid string or undefined
  const hasNextPage = !!nextCursor;
  const totalCount = tasksResponse?.totalCount;
  const canSupervise = tasksResponse?.canSupervise ?? false;

  // Update cursor history when we get a new cursor
  useEffect(() => {
    if (
      nextCursor &&
      pageIndex === cursors.length - 1 &&
      !cursors.includes(nextCursor)
    ) {
      setCursors((prev) => [...prev, nextCursor]);
    }
  }, [nextCursor, pageIndex, cursors]);

  // Calculate current page number (1-based for UI)
  const currentPage = useMemo(() => pageIndex + 1, [pageIndex]);

  // Handle page changes
  const onPageChange = useCallback(
    (newPage: number) => {
      const newPageIndex = newPage - 1;
      // Only allow navigation to pages that exist in the cursors array
      // The useEffect will add new cursors when we're on the last page and receive a nextCursor
      if (newPageIndex >= 0 && newPageIndex < cursors.length) {
        setPageIndex(newPageIndex);
      }
    },
    [cursors.length],
  );

  return {
    tasks,
    isLoading,
    isRefetching,
    currentPage,
    hasNextPage,
    onPageChange,
    totalCount,
    canSupervise,
    error,
  };
}
